import * as React from 'react';
import { Ellipsis } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export interface RowAction {
  /** Optional CSS class to apply to the action item (e.g., 'text-red-600' for destructive actions) */
  className?: string;
  /** Renders the action muted and non-interactive — visible but never fires `onClick` (e.g. a feature not implemented yet). */
  disabled?: boolean;
  /** Whether to render a separator line above this action */
  hasSeparator?: boolean;
  /** Icon to display to the left of the label */
  icon: React.ReactNode;
  /** Display label for the action */
  label: string;
  /** Click handler for the action */
  onClick: () => void;
  /** Optional permission string required to show this action. If omitted, the action is always visible. */
  requiredPermission?: string;
}

export interface RowActionsMenuProps {
  /** Array of action items to display in the popover */
  actions: RowAction[];
  /** Accessible label for the trigger button (default: 'Actions') */
  ariaLabel?: string;
  /** Optional function to check if the user has a specific permission.
   *  When provided, actions with a `requiredPermission` are only rendered
   *  if this function returns true for that permission.
   *  When not provided, all actions are rendered (backward compatible). */
  hasPermission?: (permission: string) => boolean;
}

export function RowActionsMenu({
  actions,
  ariaLabel = 'Actions',
  hasPermission,
}: RowActionsMenuProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);

  const visibleActions = hasPermission
    ? actions.filter((action) => !action.requiredPermission || hasPermission(action.requiredPermission))
    : actions;

  if (visibleActions.length === 0) {
    return <></>;
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button aria-label={ariaLabel} size="icon" variant="ghost">
          <Ellipsis />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto min-w-[12rem] p-1">
        {visibleActions.map((action, index) => (
          <React.Fragment key={`${index}_${action.label}`}>
            {action.hasSeparator && <hr className="-mx-1 my-1 h-px border-0 bg-muted" />}
            <button
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                action.disabled && 'cursor-not-allowed text-muted-foreground hover:bg-transparent',
                action.className,
              )}
              disabled={action.disabled}
              onClick={(): void => {
                if (action.disabled) return;
                action.onClick();
                setOpen(false);
              }}
              type="button"
            >
              {action.icon}
              {action.label}
            </button>
          </React.Fragment>
        ))}
      </PopoverContent>
    </Popover>
  );
}
